/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import Slider from 'react-slick'
import {
    Box,
    Heading,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Component, regionPropType} from '@salesforce/commerce-sdk-react/components'
import {BlackArrow} from '../../../components/icons'
import {popularAnime} from '../../../theme/components/project/popular-anime'

/**
 * PopularAnimeSlider Component
 *
 * @param {object} props - Component props
 * @returns {React.ReactElement}
 */
export const PopularAnimeSlider = ({
    titlePosition,
    heading,
    headingColor,
    headingStyle,
    backgroundImage,
    carouselType,
    headingFontWeight,
    regions
}) => {
    const styles = useMultiStyleConfig('ShopBySeriesLayoutStyles')
    const components = regions?.[0]?.components || []

    const NextArrow = ({className, onClick}) => (
        <Box
            className={className}
            onClick={onClick}
            tabIndex={0}
            role="button"
            onKeyDown={(e) => e.key === 'Enter' && onClick()}
            position="absolute"
            right={'4%'}
        >
            <BlackArrow />
        </Box>
    )

    const PrevArrow = ({className, onClick}) => (
        <Box
            className={className}
            onClick={onClick}
            tabIndex={0}
            role="button"
            onKeyDown={(e) => e.key === 'Enter' && onClick()}
            transform="rotate(180deg)"
            position="absolute"
            right="6%"
            mr={'80px'}
        >
            <BlackArrow />
        </Box>
    )

    const sliderSettings = {
        infinite: false,
        speed: 500,
        slidesToShow: 4.8,
        slidesToScroll: 1,
        nextArrow: <NextArrow />,
        prevArrow: <PrevArrow />,
        arrows: true,
        responsive: [
            {
                breakpoint: 1023,
                settings: {
                    slidesToShow: 2.5,
                    arrows: false
                }
            },
            {
                breakpoint: 767,
                settings: {
                    slidesToShow: 1.5,
                    arrows: false
                }
            }
        ]
    }

    return (
        <Box className="popular-anime">
            <Box
                className={`shop-by-series`}
                {...styles.shopBySeries}
                backgroundImage={`url(${backgroundImage?.url})`}
                backgroundRepeat="no-repeat"
                backgroundSize="cover"
                backgroundPosition="center"
                p={{base: '0 0 16px 16px', sm: '0 0 32px 32px', md: '0 0 32px 64px'}}
                borderTop="1px solid rgba(74, 78, 88, .3)"
                sx={popularAnime}
            >
                <Box
                    className="popular-anime-component carousel-wrapper"
                    overflowX="hidden"
                    position="relative"
                >
                    {heading && heading !== 'undefined' && (
                        <Box>
                            <Heading
                                as="h2"
                                textAlign={titlePosition == 'Text-Center' ? 'center' : 'left'}
                                fontWeight={headingFontWeight == 'Bold' ? '800' : '400'}
                                fontStyle={headingStyle}
                                fontSize={{base: '2xl', md: '40px'}}
                                lineHeight={{base: '8', md: '52px'}}
                                m={{base: '32px 0 24px', sm: '32px 0'}}
                                color={headingColor?.value}
                            >
                                {heading}
                            </Heading>
                        </Box>
                    )}
                    <Slider
                        className={`carousel-sg ${carouselType === 'Zig-Zag' ? 'zig-zag' : ''}`}
                        {...sliderSettings}
                    >
                        {components.map((component, index) => (
                            <Box key={index} data-testid="popularAnimeSlider">
                                <Component component={component} />
                            </Box>
                        ))}
                    </Slider>
                </Box>
            </Box>
        </Box>
    )
}

PopularAnimeSlider.propTypes = {
    regions: PropTypes.arrayOf(regionPropType).isRequired,
    titlePosition: PropTypes.string,
    heading: PropTypes.string,
    headingColor: PropTypes.shape({
        value: PropTypes.string
    }),
    headingStyle: PropTypes.string,
    backgroundImage: PropTypes.shape({
        url: PropTypes.string
    }),
    carouselType: PropTypes.string,
    headingFontWeight: PropTypes.string,
    onClick: PropTypes.func,
    className: PropTypes.string
}

export default PopularAnimeSlider
